/**
 * /equipment/radiation/:id — one radioactive sealed source (fieldiq-api#1334,
 * RAD-1): what it is, its decay, its leak tests and its event history, and the
 * events this person may record on it.
 *
 * EVERY FIGURE IS THE API'S. Current activity, elapsed days, leak-test status
 * and days left come back computed; the decay panel asks GET /decay?on= for a
 * chosen day rather than applying a half-life here.
 *
 * THE EVENT BUTTONS ARE A NARROWING, NOT THE RULE (lib/radiation offeredEvents).
 * The API decides under a row lock — closing events office-only (literal role),
 * a field hand checks out only to themself and checks in / transfers only what
 * they hold — and a refusal is shown in its own words. A field hand's check-out
 * sends their OWN id and offers no picker, so the page cannot ask for anything
 * the API would refuse on that ground.
 *
 * LOST IN HOLE needs a depth (the API 400s without one; the dialog says so
 * first) and notifies the company's admins the moment it is recorded.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, openUploadDownload } from '../../api/client';
import { putToPresignedUrl } from '../../api/upload';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import {
  useDeleteRadSource,
  useRadCatalog,
  useRadDecay,
  useRadSource,
  useRecordRadEvent,
  useRecordRadLeakTest,
  useUpdateRadSource,
} from '../../hooks/useRadiation';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  RAD_EVENT_LABEL,
  RAD_FORM_LABEL,
  RAD_LIST,
  RAD_RESULT_LABEL,
  canAdminRad,
  isotopeLabel,
  offeredEvents,
  radDay,
  radDaysLeft,
  radErrorMessage,
  radInstant,
  radLabel,
  radQty,
} from '../../lib/radiation';
import type { RadEventWrite, RadLeakTestWrite, RadMeta, RadSourceDetail } from '../../types/radiation';
import { RAD_ALERT, RadLeakBadge, RadSourceDialog, RadStatusBadge, useRadPeople } from '../../components/radiation/RadBits';
import { DH_BAND_SECONDARY, DH_INPUT, DH_SECONDARY, DhDialog, DhEmpty, DhField, DhPanel, DhSpec, useOpenCount } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';

const ALLOWED = 'admins, office staff, supervisors, dispatchers, field workers and logistics';

/** A client-minted uuid: the leak test's id, which its certificate upload is filed under. */
export const mintId = (): string => {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const optNum = (s: string): number | null | 'bad' => {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : 'bad';
};

const PICK_PERSON = ['check_out', 'transfer'];
const SURVEY = ['out_of_shield', 'in_hole', 'out_of_hole', 'in_shield', 'check_in', 'check_out', 'transfer'];

// ─── record an event ─────────────────────────────────────────────────────────

export function RadEventDialog({
  event,
  source,
  meta,
  role,
  userId,
  onClose,
}: {
  event: string | null;
  source: RadSourceDetail;
  meta: RadMeta | null;
  role: string | null | undefined;
  userId: string | null | undefined;
  onClose: () => void;
}) {
  const open = !!event;
  const openCount = useOpenCount(open);
  const record = useRecordRadEvent(source.id);
  const field = role === 'field_worker';
  // A field hand's check-out is to themself, so it offers no picker and sends
  // their own id (the API refuses anyone else: not_self). A transfer of a source
  // they HOLD names the colleague it goes to, so it keeps the picker.
  const pickPerson = !!event && PICK_PERSON.includes(event) && !(field && event === 'check_out');
  const people = useRadPeople(pickPerson);
  const [person, setPerson] = useState('');
  const [location, setLocation] = useState('');
  const [depth, setDepth] = useState('');
  const [survey, setSurvey] = useState('');
  const [contextRef, setContextRef] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPerson('');
    setLocation('');
    setDepth('');
    setSurvey('');
    setContextRef('');
    setNotes('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCount]);

  if (!event) return null;
  const lost = event === 'lost_in_hole';
  const depthUnit = meta?.units.depth ?? '';
  const surveyUnit = meta?.units.surveyRate ?? '';

  const submit = async () => {
    setError(null);
    const body: RadEventWrite = { event };
    if (event === 'check_out' && field) body.user_id = userId ?? null;
    else if (PICK_PERSON.includes(event)) {
      if (!person) return setError(event === 'transfer' ? 'Choose who the source is transferred to.' : 'Choose who is checking the source out.');
      body.user_id = person;
    }
    const d = optNum(depth);
    if (d === 'bad' || (typeof d === 'number' && d < 0)) return setError(`The depth must be a number of at least 0 (${depthUnit || 'tenant unit'}).`);
    if (lost && d === null) return setError(`Enter the depth the source was lost at (${depthUnit || 'tenant unit'}). It is required.`);
    if (d !== null) body.depth = d;
    const s = optNum(survey);
    if (s === 'bad' || (typeof s === 'number' && s < 0)) return setError(`The survey reading must be a number of at least 0 (${surveyUnit || 'tenant unit'}).`);
    if (s !== null) body.survey_reading = s;
    if (location.trim()) body.location = location.trim();
    if (contextRef.trim()) body.context_ref = contextRef.trim();
    if (notes.trim()) body.notes = notes.trim();
    try {
      const res = await record.mutateAsync(body);
      const n = res?.notified;
      if (lost && n) {
        toast.success(`Recorded lost in hole. ${n.inApp ?? 0} in-app and ${n.email ?? 0} email notification${(n.email ?? 0) === 1 ? '' : 's'} sent to the company's admins.`);
      } else {
        toast.success(`Recorded: ${radLabel(RAD_EVENT_LABEL, event)}.`);
      }
      onClose();
    } catch (e) {
      setError(radErrorMessage(e, 'The event could not be recorded.'));
    }
  };

  return (
    <DhDialog
      open={open}
      title={radLabel(RAD_EVENT_LABEL, event)}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={DH_SECONDARY}>Cancel</button>
          <button type="button" onClick={submit} disabled={record.isPending} className="fi-action fi-action--standard fi-action--primary">
            {record.isPending ? 'Recording…' : 'Record'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {lost && (
          <p role="note" data-testid="rad-lost-warning" className={RAD_ALERT}>
            Recording a source as lost in hole notifies the company's admins at once, by in-app notice and email.
            The source stays lost until an admin, office staff or supervisor records it recovered or abandoned in hole.
          </p>
        )}
        {event === 'check_out' && field && (
          <p className="text-sm text-fieldiq-muted" data-testid="rad-self-checkout">The source is checked out to you.</p>
        )}
        {pickPerson && (
          <DhField label={event === 'transfer' ? 'Transfer to' : 'Checked out to'}>
            <select aria-label="Person" className={DH_INPUT} value={person} onChange={(e) => setPerson(e.target.value)}>
              <option value="">{people.isLoading ? 'Loading…' : 'Choose…'}</option>
              {(people.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </DhField>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          {lost && (
            <DhField label={`Depth (${depthUnit || '—'})`} hint="Required.">
              <input aria-label="Depth" inputMode="decimal" className={DH_INPUT} value={depth} onChange={(e) => setDepth(e.target.value)} />
            </DhField>
          )}
          <DhField label="Location">
            <input aria-label="Location" className={DH_INPUT} value={location} onChange={(e) => setLocation(e.target.value)} />
          </DhField>
          {SURVEY.includes(event) && (
            <DhField label={`Survey reading (${surveyUnit || '—'})`}>
              <input aria-label="Survey reading" inputMode="decimal" className={DH_INPUT} value={survey} onChange={(e) => setSurvey(e.target.value)} />
            </DhField>
          )}
          <DhField label="Job / well reference">
            <input aria-label="Reference" className={DH_INPUT} value={contextRef} onChange={(e) => setContextRef(e.target.value)} />
          </DhField>
        </div>
        <DhField label="Notes">
          <textarea aria-label="Notes" rows={2} className={`${DH_INPUT} h-auto py-2`} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </DhField>
        {error && <p role="alert" className={RAD_ALERT}>{error}</p>}
      </div>
    </DhDialog>
  );
}

// ─── record a leak test ──────────────────────────────────────────────────────

function LeakTestDialog({ open, sourceId, onClose }: { open: boolean; sourceId: string; onClose: () => void }) {
  const openCount = useOpenCount(open);
  const record = useRecordRadLeakTest(sourceId);
  const [testId, setTestId] = useState(mintId);
  const [testedOn, setTestedOn] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [result, setResult] = useState('');
  const [measured, setMeasured] = useState('');
  const [lab, setLab] = useState('');
  const [certNo, setCertNo] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploaded, setUploaded] = useState<{ file: File; uploadId: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTestId(mintId());
    setTestedOn('');
    setDueOn('');
    setResult('');
    setMeasured('');
    setLab('');
    setCertNo('');
    setNotes('');
    setFile(null);
    setUploaded(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCount]);

  const submit = async () => {
    setError(null);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(testedOn)) return setError('Enter the day the wipe was taken.');
    if (result !== 'pass' && result !== 'fail') return setError('Choose pass or fail.');
    const m = optNum(measured);
    if (m === 'bad' || (typeof m === 'number' && m < 0)) return setError('The measured activity must be a number of at least 0 Bq.');
    setBusy(true);
    let certificateUploadId: string | null = null;
    try {
      if (file) {
        if (uploaded && uploaded.file === file) certificateUploadId = uploaded.uploadId;
        else {
          const contentType = file.type || 'application/pdf';
          const { data: presigned } = await api.uploads.getPresignedUrl({
            fileName: file.name || 'certificate.pdf', contentType, entityType: 'radiation_leak_test', entityId: testId,
          });
          // Throws unless storage answered 2xx. No Content-Type is set by hand on the
          // API calls; the interceptor owns that.
          await putToPresignedUrl(presigned.uploadUrl, file, contentType);
          await api.uploads.confirm({ key: presigned.key, size: file.size });
          if (!presigned.uploadId) throw new Error('The certificate upload did not return an id.');
          certificateUploadId = presigned.uploadId;
          setUploaded({ file, uploadId: presigned.uploadId });
        }
      }
    } catch (e) {
      setBusy(false);
      return setError(radErrorMessage(e, 'The certificate could not be uploaded. Nothing was saved.'));
    }
    const body: RadLeakTestWrite = {
      id: testId,
      tested_on: testedOn,
      due_on: dueOn || null,
      result,
      measured_bq: m,
      lab: lab.trim() || null,
      certificate_number: certNo.trim() || null,
      certificate_upload_id: certificateUploadId,
      notes: notes.trim() || null,
    };
    try {
      await record.mutateAsync(body);
      toast.success('Leak test recorded.');
      onClose();
    } catch (e) {
      setError(radErrorMessage(e, 'The leak test could not be recorded.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DhDialog
      open={open}
      title="Record a leak test"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" onClick={onClose} className={DH_SECONDARY}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy} className="fi-action fi-action--standard fi-action--primary">
            {busy ? 'Saving…' : 'Record'}
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <DhField label="Wipe taken on">
          <input aria-label="Tested on" type="date" className={DH_INPUT} value={testedOn} onChange={(e) => setTestedOn(e.target.value)} />
        </DhField>
        <DhField label="Next due" hint="Blank: the API sets it from the company's interval.">
          <input aria-label="Due on" type="date" className={DH_INPUT} value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
        </DhField>
        <DhField label="Result">
          <select aria-label="Result" className={DH_INPUT} value={result} onChange={(e) => setResult(e.target.value)}>
            <option value="">Choose…</option>
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
          </select>
        </DhField>
        <DhField label="Measured (Bq)">
          <input aria-label="Measured Bq" inputMode="decimal" className={DH_INPUT} value={measured} onChange={(e) => setMeasured(e.target.value)} />
        </DhField>
        <DhField label="Lab">
          <input aria-label="Lab" className={DH_INPUT} value={lab} onChange={(e) => setLab(e.target.value)} />
        </DhField>
        <DhField label="Certificate number">
          <input aria-label="Certificate number" className={DH_INPUT} value={certNo} onChange={(e) => setCertNo(e.target.value)} />
        </DhField>
        <DhField label="Certificate (PDF or photo)">
          <input
            aria-label="Certificate file"
            type="file"
            accept="application/pdf,image/*"
            className="block w-full text-sm text-fieldiq-text"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </DhField>
      </div>
      <div className="mt-4">
        <DhField label="Notes">
          <textarea aria-label="Notes" rows={2} className={`${DH_INPUT} h-auto py-2`} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </DhField>
      </div>
      {error && <p role="alert" className={`mt-3 ${RAD_ALERT}`}>{error}</p>}
    </DhDialog>
  );
}

// ─── decay ───────────────────────────────────────────────────────────────────

function DecayPanel({ source, unit }: { source: RadSourceDetail; unit: string }) {
  const [on, setOn] = useState('');
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(on) ? on : null;
  const decay = useRadDecay(source.id, valid);
  const failure = queryLoadFailure(decay);
  return (
    <DhPanel eyebrow="Decay" title="Activity on a day" testId="rad-decay">
      <div className="space-y-3 p-4">
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <DhSpec label="Reference activity" value={radQty(source.referenceActivity, unit)} mono />
          <DhSpec label="Reference day" value={radDay(source.referenceOn)} />
          <DhSpec label="Activity today" value={radQty(source.currentActivity, unit)} mono />
          <DhSpec label="Half-life (days)" value={radQty(source.halfLifeDays, '')} mono />
        </dl>
        <div className="flex flex-wrap items-end gap-4">
          <DhField label="Activity on">
            <input aria-label="Decay day" type="date" className={DH_INPUT} value={on} onChange={(e) => setOn(e.target.value)} />
          </DhField>
          {valid && (
            <p className="pb-2 font-mono text-sm tabular-nums text-fieldiq-text" data-testid="rad-decay-result">
              {decay.isLoading
                ? 'Calculating…'
                : failure
                  ? null
                  : `${radQty(decay.data?.activity, decay.data?.unit ?? unit)} · ${radQty(decay.data?.elapsedDays, 'days elapsed')}`}
            </p>
          )}
        </div>
        {valid && failure && <p role="alert" className={RAD_ALERT}>{radErrorMessage(queryFailureError(decay), 'The decay could not be calculated.')}</p>}
      </div>
    </DhPanel>
  );
}

// ─── the page ────────────────────────────────────────────────────────────────

export default function RadiationSourceScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList(RAD_LIST);
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canAdmin = canAdminRad(user?.role, elevated);
  const query = useRadSource(id);
  const catalogQuery = useRadCatalog();
  const update = useUpdateRadSource(id ?? '');
  const remove = useDeleteRadSource(id ?? '');
  const failure = queryLoadFailure(query);
  const source = query.data?.source ?? null;
  const meta = query.data?.meta ?? null;
  const catalog = catalogQuery.data ?? null;
  const unit = meta?.units.activity ?? '';
  const [event, setEvent] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [leakOpen, setLeakOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const title = source ? `${isotopeLabel(catalog, source.isotope, source.isotopeLabel)}${source.serialNumber ? ` · ${source.serialNumber}` : ''}` : 'Radioactive source';
  const actions = source ? offeredEvents(source, { role: user?.role, userId: user?.id, dispatcherElevated: elevated }) : [];

  const doDelete = async () => {
    setDeleteError(null);
    try {
      await remove.mutateAsync(undefined);
      toast.success('Source deleted.');
      back();
    } catch (e) {
      setDeleteError(radErrorMessage(e, 'The source could not be deleted.'));
    }
  };

  return (
    <div data-testid="radiation-source-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Radioactive source"
        title={title}
        onBack={back}
        backLabel="Back to sources"
        actions={
          source && canAdmin ? (
            <>
              <button type="button" onClick={() => setEditing(true)} className={DH_BAND_SECONDARY}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              <button type="button" onClick={() => setConfirmDelete(true)} className={DH_BAND_SECONDARY}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading source" />
            </div>
          ) : failure ? (
            <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this source" allowed={ALLOWED} />
          ) : !source ? (
            <DhEmpty>This source was not found.</DhEmpty>
          ) : (
            <>
              <DhPanel eyebrow="Source" title="What it is" testId="rad-spec">
                <dl className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4">
                  <DhSpec label="Status" value={<RadStatusBadge status={source.status} />} />
                  <DhSpec label="Form" value={radLabel(RAD_FORM_LABEL, source.sourceForm)} />
                  <DhSpec label="Serial" value={source.serialNumber ?? '—'} mono />
                  <DhSpec label="Licence" value={source.licenceNumber ?? '—'} mono />
                  <DhSpec label="Holder / container" value={source.holderDescription ?? '—'} />
                  <DhSpec label="Device" value={source.deviceName ?? '—'} />
                  <DhSpec label="Custody" value={source.custody.checkedOut ? source.custody.holderName ?? 'Checked out' : 'Not checked out'} />
                  <DhSpec label="Shielding" value={source.handling.outOfShield ? `Out of shield${source.handling.hoursOut === null ? '' : ` · ${radQty(source.handling.hoursOut, 'h')}`}` : 'In shield'} />
                  <DhSpec label="Days since reference" value={radQty(source.elapsedDays, '')} mono />
                  <DhSpec label="Leak test" value={<RadLeakBadge status={source.leakTest.status} />} />
                  <DhSpec label="Leak test due" value={`${radDay(source.leakTest.dueOn)} · ${radDaysLeft(source.leakTest.daysLeft)}`} />
                  <DhSpec label="Notes" value={source.notes ?? '—'} />
                </dl>
              </DhPanel>

              <DhPanel eyebrow="Custody and handling" title="Record an event" testId="rad-actions">
                <div className="p-4">
                  {actions.length === 0 ? (
                    <p className="text-sm text-fieldiq-muted">No event can be recorded on this source by your role in its current state.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {actions.map((a) => (
                        <button key={a} type="button" onClick={() => setEvent(a)} className={DH_SECONDARY} data-testid={`rad-action-${a}`}>
                          {radLabel(RAD_EVENT_LABEL, a)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </DhPanel>

              <DecayPanel source={source} unit={unit} />

              <DhPanel
                eyebrow="Leak tests"
                title="Wipe tests"
                testId="rad-leak-tests"
                actions={
                  canAdmin ? (
                    <button type="button" onClick={() => setLeakOpen(true)} className={DH_SECONDARY}>
                      <Plus className="size-4" aria-hidden="true" />
                      Record leak test
                    </button>
                  ) : undefined
                }
              >
                {source.leakTests.length === 0 ? (
                  <DhEmpty>No leak test is recorded for this source.</DhEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left text-sm">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr>
                          <th scope="col" className="px-4 py-3">Taken</th>
                          <th scope="col" className="px-4 py-3">Due</th>
                          <th scope="col" className="px-4 py-3">Result</th>
                          <th scope="col" className="px-4 py-3">Measured</th>
                          <th scope="col" className="px-4 py-3">Lab</th>
                          <th scope="col" className="px-4 py-3">Certificate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {source.leakTests.map((t) => (
                          <tr key={t.id} className="border-t border-fieldiq-border text-fieldiq-text" data-testid="rad-leak-row">
                            <td className="px-4 py-3">{radDay(t.testedOn)}</td>
                            <td className="px-4 py-3">{radDay(t.dueOn)}</td>
                            <td className="px-4 py-3">{radLabel(RAD_RESULT_LABEL, t.result)}</td>
                            <td className="px-4 py-3 font-mono tabular-nums">{radQty(t.measuredBq, 'Bq')}</td>
                            <td className="px-4 py-3">{t.lab ?? '—'}</td>
                            <td className="px-4 py-3">
                              {t.certificateUploadId ? (
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 font-bold text-fi-amber hover:underline"
                                  onClick={() => openUploadDownload(t.certificateUploadId!).catch((e) => toast.error(radErrorMessage(e, 'The certificate could not be opened.')))}
                                >
                                  <FileText className="size-4" aria-hidden="true" />
                                  {t.certificateNumber ?? 'Open certificate'}
                                </button>
                              ) : (
                                t.certificateNumber ?? '—'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DhPanel>

              <DhPanel eyebrow="History" title="Events" testId="rad-events">
                {source.events.length === 0 ? (
                  <DhEmpty>No event is recorded for this source.</DhEmpty>
                ) : (
                  <ol className="divide-y divide-fieldiq-border">
                    {source.events.map((e) => (
                      <li key={e.id} className="px-4 py-3 text-sm text-fieldiq-text" data-testid="rad-event-row">
                        <p className="font-extrabold text-white">
                          {radLabel(RAD_EVENT_LABEL, e.event)}
                          <span className="ml-2 font-normal text-fieldiq-muted">{radInstant(e.occurredAt, meta?.timeZone)}</span>
                        </p>
                        <p className="mt-1 text-xs text-fieldiq-muted">
                          {[
                            e.userName ? `Person: ${e.userName}` : null,
                            e.location ? `Location: ${e.location}` : null,
                            e.depth !== null ? `Depth: ${radQty(e.depth, meta?.units.depth)}` : null,
                            e.surveyReading !== null ? `Survey: ${radQty(e.surveyReading, meta?.units.surveyRate)}` : null,
                            e.contextRef ? `Ref: ${e.contextRef}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </p>
                        {e.notes && <p className="mt-1 text-xs text-fieldiq-text">{e.notes}</p>}
                      </li>
                    ))}
                  </ol>
                )}
              </DhPanel>
            </>
          )}
        </div>
      </section>

      {source && (
        <RadEventDialog event={event} source={source} meta={meta} role={user?.role} userId={user?.id} onClose={() => setEvent(null)} />
      )}
      {source && canAdmin && (
        <>
          <RadSourceDialog
            open={editing}
            onClose={() => setEditing(false)}
            catalog={catalog}
            activityUnit={unit}
            source={source}
            pending={update.isPending}
            onSubmit={(body) => update.mutateAsync(body)}
          />
          <LeakTestDialog open={leakOpen} sourceId={source.id} onClose={() => setLeakOpen(false)} />
          <DhDialog
            open={confirmDelete}
            title="Delete this source?"
            onClose={() => setConfirmDelete(false)}
            footer={
              <>
                <button type="button" onClick={() => setConfirmDelete(false)} className={DH_SECONDARY}>Cancel</button>
                <button type="button" onClick={doDelete} disabled={remove.isPending} className="fi-action fi-action--standard fi-action--primary">
                  {remove.isPending ? 'Deleting…' : 'Delete'}
                </button>
              </>
            }
          >
            <p className="text-sm text-fieldiq-text">
              A source registered in error can be deleted. One that is checked out must be checked in first, and one lost in hole must be recorded recovered or abandoned first.
            </p>
            {deleteError && <p role="alert" className={`mt-3 ${RAD_ALERT}`}>{deleteError}</p>}
          </DhDialog>
        </>
      )}
    </div>
  );
}
